import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useBoolean } from 'ahooks';
import { Button } from 'tea-component';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface ICollapsibleMultiTextsProps extends IBaseProps {
  // 是否展开
  isShowAll?: boolean;
  // 文本
  text: string;
  // 默认展示的行数，超过则折叠
  rows?: number;
  // 文本行高
  lineHeight?: number | string;
  // 展开后是否隐藏折叠按钮
  hideCollapseBtnAfterExpand?: boolean;
  // 展开按钮的文案
  expandBtnText?: string;
  // 折叠按钮的文案
  collapseBtnText?: string;
  // 获取是否超出隐藏，展示...
  getIsOverflow?: (payload: boolean) => void;
}

function useIsOverflow(
  node: React.RefObject<HTMLDivElement | null>,
  props: { isShowAll: boolean; text: string; getIsOverflow?: (payload: boolean) => void },
) {
  const { isShowAll, text, getIsOverflow } = props;

  const [isOverflow, setIsOverflow] = useState(false);

  const updateIsOverflow = () => {
    setIsOverflow(false);
    getIsOverflow?.(false);
    if (node.current) {
      const { clientHeight, scrollHeight } = node.current;
      const isOverflowTmp = scrollHeight > clientHeight;
      setIsOverflow(isOverflowTmp);
      getIsOverflow?.(isOverflowTmp);
    }
  };

  useEffect(() => {
    updateIsOverflow();

    window.addEventListener('resize', updateIsOverflow, false);

    return () => {
      window.removeEventListener('resize', updateIsOverflow, false);
    };
  }, [text, isShowAll]);

  return isOverflow;
}

const CollapsibleMultiTexts: React.FC<ICollapsibleMultiTextsProps> = (props) => {
  const {
    style,
    className,
    isShowAll: isShowAllProps = false,
    text,
    rows = 4,
    lineHeight = '20px',
    hideCollapseBtnAfterExpand = true,
    expandBtnText = 'Show All',
    collapseBtnText = 'Show Less',
    placeholder = '-',
    getIsOverflow,
  } = props;

  const textTrim = text?.trim();
  const isEmpty = !textTrim;

  const [isShowAll, { toggle: toggleIsShowAll, set: setIsShowAll }] = useBoolean(isShowAllProps);

  const btnText = isShowAll ? collapseBtnText : expandBtnText;

  const node = useRef<HTMLDivElement | null>(null);

  const isOverflow = useIsOverflow(node, { isShowAll, text, getIsOverflow });

  const [isShowBtn, setIsShowBtn] = useState(true);

  useEffect(() => {
    setIsShowBtn(!isShowAllProps);
    setIsShowAll(isShowAllProps);
  }, [isShowAllProps]);

  return (
    <div style={style} className={classnames('dbg-collapsible-multi-texts', className)}>
      {isEmpty && <span>{placeholder}</span>}
      {!isEmpty && (
        <>
          <div
            ref={node}
            style={{ lineHeight, WebkitLineClamp: isShowAll ? 'initial' : rows }}
            className="dbg-collapsible-multi-texts__context dbg-table-line-height-22"
          >
            {textTrim}
          </div>
          {isOverflow && isShowBtn && (
            <Button
              className="dbg-collapsible-multi-texts__toggle-btn"
              type="link"
              onClick={(e) => {
                e?.stopPropagation();
                toggleIsShowAll();
                if (hideCollapseBtnAfterExpand && !isShowAll) {
                  setIsShowBtn(false);
                }
              }}
            >
              {btnText}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default CollapsibleMultiTexts;
